import type { MouseEvent } from "react";

/**
 * Shared guard for card/row "title" links that must stay real anchors (so
 * right-click → "open in new tab" and middle-click work) while still routing
 * plain left-clicks through the app's existing SPA navigation callback rather
 * than a full page load.
 *
 * Right-click never reaches a click handler at all (it fires contextmenu, not
 * click), and middle-click fires auxclick, not click — so this only needs to
 * separate a plain left-click from a modified one (ctrl/cmd/shift/alt+click),
 * which browsers still deliver as a "click" event.
 *
 * Accepted trade-off (as of #631, converting these controls from <button> to
 * <a>): a native <a> activates on Enter only, not Space, unlike the <button>
 * these controls used to be. That's native anchor behavior, not something
 * this guard can or should work around — if a "keyboard broken" report ever
 * traces back to Space not activating one of these links, this is why.
 */
export function isPlainLeftClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * The click handler every SPA link in this app needs: on a plain left-click,
 * block the full-page load and run `navigate` instead; on anything else
 * (modified click, or `navigate` itself absent), do nothing and let the
 * browser's native anchor behavior — new tab, copy link, etc. — take over.
 *
 * `stopPropagation` is for a link nested inside a row/card that has its own
 * click handler (e.g. PluginListView's full-row click): it must run
 * unconditionally, before the plain-left-click guard, or a modified click
 * would still fall through and fire the row's handler alongside the
 * browser's own new-tab navigation.
 */
export function onSpaLinkClick(
  navigate: (() => void) | undefined,
  opts?: { stopPropagation?: boolean },
) {
  return (e: MouseEvent) => {
    if (opts?.stopPropagation) e.stopPropagation();
    if (!isPlainLeftClick(e) || !navigate) return;
    e.preventDefault();
    navigate();
  };
}
